// 介錯の FX を「コードで描いた感」から絵の質感へ替える素材 (2026-10-29・作者の答え exec-fx-q1〜q5)。
//
// 作者 (10-29): 剥き出しのネオン風 (輪郭の発光線・灰色の写し・白い弧・赤い筋) が「コードで描いた感」で嫌。
// 分け方は exec_fx_sprites.ts と同じ: 質感は Codex が描いた絵 (public/fx/exec/mats/)、出る・動く・消えるはコード。
// 絵の元 = そざい/concept/exec-fx-1029/sprites/ (票 exec-fx-mats-a-1029 / exec-fx-mats-b-1029)。
// 切り出し = scripts/slice_exec_fx_mats.py。
//   マスクの絵 (靄・土ぼこり・振りのぶれ) = 白＋アルファ (明るさ)。色はコードで付ける (tinted)。
//   色付きの絵 (焦げ跡・煙・砲弾・ひび) = そのまま置く。
// 絵が読めていない間 (読み込み中・Vitest) は null を返し、呼ぶ側は描かないか、前の版で描く。

type Sheet = { names: readonly string[]; imgs: (HTMLImageElement | null)[] }

function sheet(prefix: string, rows: readonly (readonly [string, number])[]): Sheet {
  const names = rows.flatMap(([row, n]) => Array.from({ length: n }, (_, i) => `${prefix}_${row}${i}`))
  return { names, imgs: names.map(() => null) }
}

// 靄: 形4種 × 時間3コマ (濃い→ほどける→薄れる)。根元 = 下辺の真ん中。
export const MIST_FORMS = 4
export const MIST_STEPS = 3
const mist = sheet('mist', Array.from({ length: MIST_FORMS }, (_, f) => [`${f}_`, MIST_STEPS] as const))
// 土ぼこり: 蹴り上げ4コマ・着地で左右へ4コマ・かけら4種。根元 = 下辺の真ん中。
const dust = sheet('dust', [['kick_', 4], ['land_', 4], ['bit_', 4]])
// 焦げ跡: 育つ4段。煙4種。焦げの中心 = 真ん中・煙の根元 = 下辺の真ん中。
const scorch = sheet('scorch', [['', 4], ['smoke_', 4]])
// 砲弾: 右向き。長い尾4コマ・短い尾4コマ。弾の中心 = 右から 14%・高さの真ん中。
const shell = sheet('shell', [['long_', 4], ['short_', 4]])
// 空を飛ぶ榴弾 (11-01): 上の砲弾の絵の玉の位置と大きさを揃え、玉は1つの絵を全コマに貼った版 (scripts/bake_exec_shell_fly_1101.py)。
//   元の8枚は玉の大きさと位置がコマごとに違い (半径 14〜17.5px・2枚は右端で玉が切れる)、18 コマ/秒で玉が震えた。
const shellFly = sheet('shell', [['fly_long_', 4], ['fly_short_', 4]])
// 振りのぶれ: 振り下ろし4コマ・薙ぎ払い4コマ。右向き・振りの中心 = 左下。
const swing = sheet('swing', [['down_', 4], ['sweep_', 4]])
// ひび: 伸びる4段・かけら4種。左端から右へ伸びる。
const crack = sheet('crack', [['', 4], ['bit_', 4]])
// 咆哮の塵の帯 (10-31・票 exec-roar-dustwall-1031): 立ち上がる→最も高い→前へ倒れる→沈む の4コマ。右へ進む・下端 = 床。
const roarWall = sheet('roar', [['wall_', 4]])

const ALL = [mist, dust, scorch, shell, shellFly, swing, crack, roarWall]
// 砲弾の絵: 玉は右端寄り (幅の SHELL_CORE_X)、火の尾は左へ。玉の半径は絵の幅の SHELL_CORE_R。
export const SHELL_CORE_X = 0.89
export const SHELL_CORE_R = 0.09
// 揃えた版 (288x176): 玉の中心 = (256, 88)・半径 16px。
export const SHELL_FLY_CORE_X = 256 / 288
export const SHELL_FLY_CORE_R = 16 / 288

// 振りの跡の見た目 (作者 10-29 q1「AI に任せる」): 筆で掃いたぶれの絵。
// 10-30 作者 (弾き)「ちょっと大げさすぎる。もう少し妥当に」→ 控えめ版 lite。同じ筆の絵を前の白い弧ほどの細さ・薄さで置く。
//   10-30 作者 parry2「入れる」で lite が配布版の既定。前の白い弧は DEV ?execSwing=line・太い筆は ?execSwing=brush。
export type ExecSwingLook = 'brush' | 'lite' | 'line'
let swingLook: ExecSwingLook = 'lite'
export function setExecSwingLook(v: string | null): void { swingLook = v === 'brush' || v === 'line' ? v : 'lite' }
export function execSwingBrushOn(): boolean { return swingLook !== 'line' && ready(swing) }
export function execSwingLite(): boolean { return swingLook === 'lite' }

let started = false
/** 素材の読み込みを始める (render の preload から1回)。 */
export function preloadExecFxMats(prefix = '/fx/exec/mats/'): void {
  if (started || typeof Image === 'undefined') return
  started = true
  for (const s of ALL) {
    s.names.forEach((name, i) => {
      const img = new Image()
      img.onload = () => { s.imgs[i] = img }
      img.src = `${prefix}${name}.png`
    })
  }
}

function pick(s: Sheet, row: string, i: number, prefix: string): HTMLImageElement | null {
  const idx = s.names.indexOf(`${prefix}_${row}${i}`)
  return idx < 0 ? null : s.imgs[idx]!
}
function ready(s: Sheet): boolean { return s.imgs.every(i => i !== null) }

const wrap = (i: number, n: number): number => ((Math.floor(i) % n) + n) % n
export function mistReady(): boolean { return ready(mist) }
export function mistSprite(form: number, step: number): HTMLImageElement | null {
  return pick(mist, `${wrap(form, MIST_FORMS)}_`, Math.max(0, Math.min(MIST_STEPS - 1, step)), 'mist')
}
export function dustReady(): boolean { return ready(dust) }
export function dustSprite(kind: 'kick' | 'land' | 'bit', i: number): HTMLImageElement | null {
  return pick(dust, `${kind}_`, kind === 'bit' ? wrap(i, 4) : Math.max(0, Math.min(3, i)), 'dust')
}
export function scorchReady(): boolean { return ready(scorch) }
export function scorchSprite(stage: number): HTMLImageElement | null { return pick(scorch, '', Math.max(0, Math.min(3, stage)), 'scorch') }
export function smokeSprite(i: number): HTMLImageElement | null { return pick(scorch, 'smoke_', wrap(i, 4), 'scorch') }
export function shellReady(): boolean { return ready(shell) }
export function shellSprite(long: boolean, i: number): HTMLImageElement | null { return pick(shell, long ? 'long_' : 'short_', wrap(i, 4), 'shell') }
export function shellFlyReady(): boolean { return ready(shellFly) }
export function shellFlySprite(long: boolean, i: number): HTMLImageElement | null { return pick(shellFly, long ? 'fly_long_' : 'fly_short_', wrap(i, 4), 'shell') }
export function swingReady(): boolean { return ready(swing) }
export function swingSprite(kind: 'down' | 'sweep', i: number): HTMLImageElement | null {
  return pick(swing, `${kind}_`, Math.max(0, Math.min(3, i)), 'swing')
}
export function roarWallReady(): boolean { return ready(roarWall) }
export function roarWallSprite(stage: number): HTMLImageElement | null { return pick(roarWall, 'wall_', Math.max(0, Math.min(3, stage)), 'roar') }
export function crackReady(): boolean { return ready(crack) }
export function crackSprite(stage: number): HTMLImageElement | null { return pick(crack, '', Math.max(0, Math.min(3, stage)), 'crack') }
export function crackBitSprite(i: number): HTMLImageElement | null { return pick(crack, 'bit_', wrap(i, 4), 'crack') }

// マスクの絵に色を付けた写しを、絵と色ごとに1回だけ作って持つ。
const tintCache = new WeakMap<HTMLImageElement, Map<string, HTMLCanvasElement>>()
/** 白＋アルファの絵を rgb ('r,g,b') で塗った写しを返す。canvas を作れない所では元の絵を返す。 */
export function tinted(img: HTMLImageElement, rgb: string): CanvasImageSource {
  if (typeof document === 'undefined') return img
  let byColor = tintCache.get(img)
  if (!byColor) { byColor = new Map(); tintCache.set(img, byColor) }
  const hit = byColor.get(rgb)
  if (hit) return hit
  const c = document.createElement('canvas')
  c.width = img.naturalWidth; c.height = img.naturalHeight
  const g = c.getContext('2d')
  if (!g) return img
  g.drawImage(img, 0, 0)
  g.globalCompositeOperation = 'source-in'
  g.fillStyle = `rgb(${rgb})`
  g.fillRect(0, 0, c.width, c.height)
  byColor.set(rgb, c)
  return c
}

/** 絵を置く。(ax, ay) = 絵の中の基準点 (0..1)。op は既定で普通の重ね。 */
export function drawMat(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, x: number, y: number, w: number, h: number,
  ax: number, ay: number, alpha: number, rot = 0, flipX = false, op: GlobalCompositeOperation = 'source-over',
): void {
  if (alpha <= 0.01 || w <= 0.5 || h <= 0.5) return
  ctx.save()
  ctx.globalCompositeOperation = op
  ctx.globalAlpha = Math.min(1, alpha)
  ctx.translate(x, y)
  if (rot) ctx.rotate(rot)
  if (flipX) ctx.scale(-1, 1)
  ctx.drawImage(img, -w * ax, -h * ay, w, h)
  ctx.restore()
}
